import { useCallback, useReducer } from 'react';

export interface CarouselState<TItem> {
  currentIndex: number;
  visibleItems: TItem[];
}

export type CarouselAction = { type: 'increment' } | { type: 'decrement' } | { type: 'jumpTo'; payload: number };

function slidingWindow<TItem>(index: number, items: TItem[]) {
  return [items.at(index - 1), items.at(index), items.at((index + 1) % items.length)].filter(Boolean) as TItem[];
}

export function useCarousel<TItem>(originalItems: TItem[] = []) {
  const [state, dispatch] = useReducer(
    (state: CarouselState<TItem>, action: CarouselAction) => {
      if (action.type === 'increment') {
        const newIndex = (state.currentIndex + 1) % originalItems.length;
        return { visibleItems: slidingWindow(newIndex, originalItems), currentIndex: newIndex };
      }
      if (action.type === 'decrement') {
        const newIndex = state.currentIndex === 0 ? originalItems.length - 1 : state.currentIndex - 1;
        return { visibleItems: slidingWindow(newIndex, originalItems), currentIndex: newIndex };
      }
      if (action.type === 'jumpTo') {
        return { visibleItems: slidingWindow(action.payload, originalItems), currentIndex: action.payload };
      }
      return state;
    },
    { currentIndex: 0, visibleItems: slidingWindow(0, originalItems) }
  );

  const increment = useCallback(() => {
    dispatch({ type: 'increment' });
  }, [dispatch]);

  const decrement = useCallback(() => {
    dispatch({ type: 'decrement' });
  }, [dispatch]);

  const jumpTo = useCallback(
    (index: number) => {
      dispatch({ type: 'jumpTo', payload: index });
    },
    [dispatch]
  );

  return { increment, decrement, jumpTo, state };
}
